import { Alpine } from '../../core/alpine-core.js';
import { apiFetch } from '../api.js';
import { venuePersonaAvatarDataUri } from '../../public/venue-kiosk-avatars.js';
import {
  canManageTournamentDoc,
  canDeleteTournamentDoc,
  canEndTournamentSessionDoc,
} from './tournament-access.js';

function navTo(path) {
  const router = globalThis.PineconeRouter;
  if (router?.navigate) router.navigate(path);
  else window.location.assign(path);
}

function idStr(v) {
  return v == null ? '' : String(v);
}

Alpine.data('tournamentWizardPage', () => ({
  loading: true,
  saving: false,
  generating: false,
  deleting: false,
  ending: false,
  error: '',
  isNewRoute: false,
  tournamentId: '',
  tournament: null,
  entries: [],
  step: 1,
  totalSteps: 3,
  playerOptions: [],
  teamOptions: [],
  form: {
    title: '',
    format: 'single_elimination',
    entryType: 'player',
    notes: '',
  },
  /** Player/team ids ticked in entrant search (multi-add). */
  entrantPickSelected: {},
  entrantSearch: { q: '', open: false, busy: false, apiResults: [], useDirectory: false },
  entrantDirectoryFilterLevel: '',
  entrantDirectoryMinAvg: 0,
  entrantDirectoryLevelOptions: ['beginner', 'intermediate', 'advanced', 'pro'],
  quickAddPlayer: {
    lookupQuery: '',
    lookupOpen: false,
    lookupBusy: false,
    lookupResults: [],
    fullName: '',
    email: '',
    sendInvite: false,
    busy: false,
  },
  /** Pair flow: pick two players before add. */
  pairPick: { a: '', b: '' },
  venueOptions: [],
  drawConfig: {
    startMode: 'now',
    scheduledStartAt: '',
    defaultVenueId: '',
    defaultBoardId: '',
    seedMode: 'by_seed',
    balanceByes: true,
    groupCount: 2,
    knockoutSize: 8,
    grandFinal: 'simple',
    playMode: 'local',
    matchRules: {
      gameType: 'legs',
      startingScore: 501,
      bestOfLegs: 3,
      bestOfSets: 3,
      legsPerSet: 3,
      doubleOut: true,
      doubleIn: false,
    },
  },
  startingScoreOptions: [301, 501, 701, 1001],
  bestOfLegsOptions: [1, 3, 5, 7, 9, 11, 13],
  bestOfSetsOptions: [1, 3, 5, 7, 9],
  legsPerSetOptions: [1, 3, 5, 7, 9],

  async init() {
    this.loading = true;
    this.error = '';
    try {
      const path = window.location.pathname || '';
      this.isNewRoute = /\/tournaments\/new\/?$/.test(path.replace(/\/+$/, '') || '');
      const m = /\/tournaments\/([^/]+)\/builder/.exec(path);
      if (!this.isNewRoute && m) {
        this.tournamentId = decodeURIComponent(m[1]);
        await Promise.all([this.loadTournamentBundle(), this.loadPickLists(), this.loadVenues()]);
        await this.ensureSelfEntered();
      } else if (this.isNewRoute) {
        /* new — basics only */
      } else {
        this.error = 'Invalid builder URL';
      }
    } catch (e) {
      this.error = e.message || String(e);
    } finally {
      this.loading = false;
    }
  },

  async loadPickLists() {
    const [pr, tr] = await Promise.all([
      apiFetch('/players?limit=200&sortBy=fullName:asc&includeRemoved=false&select=_id,fullName,nickname,email,imageUrl'),
      apiFetch('/teams?limit=200&sortBy=name:asc&active=true&select=_id,name,logoUrl,photoUrl'),
    ]);
    const pj = await pr.json().catch(() => ({}));
    const tj = await tr.json().catch(() => ({}));
    if (!pr.ok) {
      throw new Error(pj.message || pj.error || `Could not load players (${pr.status})`);
    }
    if (!tr.ok) {
      throw new Error(tj.message || tj.error || `Could not load teams (${tr.status})`);
    }
    this.playerOptions = (Array.isArray(pj.items) ? pj.items : []).map((p) => ({
      ...p,
      _id: idStr(p._id || p.id),
    }));
    this.teamOptions = (Array.isArray(tj.items) ? tj.items : []).map((t) => ({
      ...t,
      _id: idStr(t._id || t.id),
    }));
  },

  async loadTournamentBundle() {
    const res = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.ok === false) throw new Error(data.error || 'Failed to load tournament');
    this.tournament = data.tournament;
    this.entries = Array.isArray(data.entries) ? data.entries : [];
    this.form.title = this.tournament.title || '';
    this.form.format = this.tournament.format || 'single_elimination';
    this.form.entryType = this.tournament.entryType || 'player';
    this.form.notes = this.tournament.notes || '';
    this.hydrateDrawConfig(this.tournament?.drawConfig);
  },

  async loadVenues() {
    try {
      const res = await apiFetch('/venues?limit=100');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      this.venueOptions = (Array.isArray(data.items) ? data.items : Array.isArray(data.venues) ? data.venues : [])
        .map((v) => ({
          _id: idStr(v._id || v.id),
          name: String(v.name || v.title || v._id || '').trim() || 'Venue',
        }))
        .filter((v) => v._id);
    } catch {
      this.venueOptions = [];
    }
  },

  hydrateDrawConfig(raw) {
    const dc = raw && typeof raw === 'object' ? raw : {};
    const mr = dc.matchRules && typeof dc.matchRules === 'object' ? dc.matchRules : {};
    this.drawConfig = {
      startMode: dc.startMode === 'scheduled' ? 'scheduled' : 'now',
      scheduledStartAt: dc.scheduledStartAt ? this.toDatetimeLocal(dc.scheduledStartAt) : '',
      defaultVenueId: idStr(dc.defaultVenueId),
      defaultBoardId: String(dc.defaultBoardId || '').trim(),
      seedMode: dc.seedMode === 'random' ? 'random' : 'by_seed',
      balanceByes: dc.balanceByes !== false,
      groupCount: Number(dc.groupCount) > 0 ? Number(dc.groupCount) : 2,
      knockoutSize: Number(dc.knockoutSize) > 0 ? Number(dc.knockoutSize) : 8,
      grandFinal: ['none', 'simple', 'double'].includes(dc.grandFinal) ? dc.grandFinal : 'simple',
      playMode: dc.playMode === 'remote' ? 'remote' : 'local',
      matchRules: {
        gameType: mr.gameType === 'sets' ? 'sets' : 'legs',
        startingScore: Number(mr.startingScore) || 501,
        bestOfLegs: Number(mr.bestOfLegs) || 3,
        bestOfSets: Number(mr.bestOfSets) || 3,
        legsPerSet: Number(mr.legsPerSet) || 3,
        doubleOut: mr.doubleOut !== false,
        doubleIn: !!mr.doubleIn,
      },
    };
    this.normalizeMatchRulesLocal();
  },

  toDatetimeLocal(iso) {
    const d = new Date(String(iso || ''));
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  },

  fromDatetimeLocal(val) {
    const raw = String(val || '').trim();
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  },

  formatLabel(fmt) {
    const m = {
      single_elimination: 'Single elimination',
      double_elimination: 'Double elimination',
      round_robin: 'Round robin',
      group_knockout: 'Group stage + knockout',
    };
    return m[String(fmt || '')] || String(fmt || '').replace(/_/g, ' ');
  },

  entryTypeLabel(t) {
    const m = { player: 'Individual players', team: 'Teams', pair: 'Pairs (doubles)' };
    return m[String(t || '')] || String(t || '');
  },

  activeEntries() {
    return (this.entries || []).filter((e) => !e.withdrawn);
  },

  canManage() {
    return canManageTournamentDoc(this.tournament, this.$store?.session?.me);
  },

  canDelete() {
    return canDeleteTournamentDoc(this.tournament, this.$store?.session?.me);
  },

  canEndSession() {
    return canEndTournamentSessionDoc(this.tournament, this.$store?.session?.me);
  },

  myPlayerId() {
    const me = this.$store?.session?.me || {};
    return String(
      this.$store?.session?.rosterSelfPlayerId || me.playerId || me.player?._id || '',
    ).trim();
  },

  async ensureSelfEntered() {
    if (!this.tournamentId) return;
    if (this.form.entryType !== 'player') return;
    if (!['draft', 'registration'].includes(String(this.tournament?.phase || ''))) return;
    const pid = this.myPlayerId();
    if (!pid) return;
    if (this.isPlayerInEntries(pid)) return;
    try {
      const body = this.buildEntryBodyForPlayer(pid);
      const entry = await this.postEntry(body);
      this.entries.push(entry);
      this.$store?.toastr?.info?.('You were added to the tournament', 4000);
    } catch {
      /* optional — may fail if phase changed concurrently */
    }
  },

  startModeLabel() {
    return this.drawConfig.startMode === 'scheduled' ? 'Scheduled start' : 'Play now';
  },

  seedModeLabel() {
    return this.drawConfig.seedMode === 'random' ? 'Random draw' : 'By seed order';
  },

  sanitizeMatchOddOption(raw, values, fallback = 3) {
    const n = Number(raw);
    if (!Number.isFinite(n)) return fallback;
    if (values.includes(n)) return n;
    return values.reduce((best, cur) => (Math.abs(cur - n) < Math.abs(best - n) ? cur : best), fallback);
  },

  normalizeMatchRulesLocal() {
    const m = this.drawConfig?.matchRules;
    if (!m) return;
    m.bestOfLegs = this.sanitizeMatchOddOption(m.bestOfLegs, this.bestOfLegsOptions, 3);
    m.bestOfSets = this.sanitizeMatchOddOption(m.bestOfSets, this.bestOfSetsOptions, 3);
    m.legsPerSet = this.sanitizeMatchOddOption(m.legsPerSet, this.legsPerSetOptions, 3);
    const ss = Number(m.startingScore);
    m.startingScore = this.startingScoreOptions.includes(ss) ? ss : 501;
  },

  setMatchGameType(type) {
    this.drawConfig.matchRules.gameType = String(type) === 'sets' ? 'sets' : 'legs';
  },

  setMatchStartingScore(score) {
    const n = Number(score);
    if (this.startingScoreOptions.includes(n)) {
      this.drawConfig.matchRules.startingScore = n;
    }
  },

  setMatchBestOfLegs(value) {
    this.drawConfig.matchRules.bestOfLegs = this.sanitizeMatchOddOption(value, this.bestOfLegsOptions, 3);
  },

  bumpMatchBestOfLegs(delta) {
    const values = this.bestOfLegsOptions;
    const current = this.sanitizeMatchOddOption(this.drawConfig.matchRules.bestOfLegs, values, 3);
    const idx = Math.max(0, values.indexOf(current));
    const next = Math.max(0, Math.min(values.length - 1, idx + (delta > 0 ? 1 : -1)));
    this.drawConfig.matchRules.bestOfLegs = values[next];
  },

  setMatchBestOfSets(value) {
    this.drawConfig.matchRules.bestOfSets = this.sanitizeMatchOddOption(value, this.bestOfSetsOptions, 3);
  },

  setMatchLegsPerSet(value) {
    this.drawConfig.matchRules.legsPerSet = this.sanitizeMatchOddOption(value, this.legsPerSetOptions, 3);
  },

  legsToWinRace(count) {
    const n = Number(count) || 3;
    return Math.floor(n / 2) + 1;
  },

  matchSetsRaceHint() {
    const m = this.drawConfig.matchRules || {};
    if (m.gameType !== 'sets') return '';
    const setsWin = this.legsToWinRace(m.bestOfSets);
    const legsWin = this.legsToWinRace(m.legsPerSet);
    return `First to ${setsWin} sets · ${legsWin} legs to win each set`;
  },

  matchRuleChipClass(active) {
    return active
      ? 'border-emerald-400/50 bg-emerald-500/25 text-emerald-100'
      : 'border-white/10 bg-white/[0.04] text-neutral-300 hover:bg-white/[0.07]';
  },

  matchRulesSummary() {
    const m = this.drawConfig.matchRules || {};
    const parts = [`${Number(m.startingScore) || 501} start`];
    if (m.gameType === 'sets') {
      parts.push(`best of ${Number(m.bestOfSets) || 3} sets`);
      parts.push(`${Number(m.legsPerSet) || 3} legs per set`);
    } else {
      parts.push(`best of ${Number(m.bestOfLegs) || 3} legs`);
    }
    const rules = [];
    if (m.doubleIn) rules.push('double in');
    if (m.doubleOut !== false) rules.push('double out');
    if (rules.length) parts.push(rules.join(', '));
    return parts.join(' · ');
  },

  drawOptionsSummary() {
    const lines = [this.seedModeLabel()];
    if (this.drawConfig.balanceByes !== false) lines.push('Balance byes');
    if (this.form.format === 'round_robin' || this.form.format === 'group_knockout') {
      lines.push(`${this.drawConfig.groupCount} group(s)`);
    }
    if (this.form.format === 'group_knockout') {
      lines.push(`Knockout bracket: ${this.drawConfig.knockoutSize}`);
    }
    if (this.form.format === 'double_elimination') {
      const gf = { none: 'No grand final', simple: 'Simple grand final', double: 'Double grand final' };
      lines.push(gf[this.drawConfig.grandFinal] || 'Grand final');
    }
    return lines;
  },

  scheduleSummary() {
    if (this.drawConfig.startMode === 'scheduled') {
      const when = this.fromDatetimeLocal(this.drawConfig.scheduledStartAt);
      return when
        ? new Date(when).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
        : 'Date/time not set';
    }
    return 'Matches ready to play as soon as the bracket is generated';
  },

  venueSummary() {
    const vid = idStr(this.drawConfig.defaultVenueId);
    if (!vid) return 'Any venue';
    const v = this.venueOptions.find((x) => idStr(x._id) === vid);
    const board = String(this.drawConfig.defaultBoardId || '').trim();
    return board ? `${v?.name || vid} · Board ${board}` : (v?.name || vid);
  },

  canGoToStep(target) {
    const n = Number(target);
    if (n === 2 && this.activeEntries().length < 2) {
      this.$store?.toastr?.warning?.('Add at least 2 entrants before draw options');
      return false;
    }
    if (n === 3 && !this.validateDrawConfig(false)) return false;
    return true;
  },

  async goToStep(target) {
    const n = Number(target);
    if (!Number.isFinite(n) || n < 1 || n > this.totalSteps) return;
    if (n > this.step && !this.canGoToStep(n)) return;
    if (this.step === 2 && n === 3) {
      const ok = await this.saveDrawOptions();
      if (!ok) return;
    }
    this.step = n;
    if (n === 3) window.scrollTo?.({ top: 0, behavior: 'smooth' });
  },

  validateDrawConfig(showToast = true) {
    if (this.drawConfig.startMode === 'scheduled' && !this.fromDatetimeLocal(this.drawConfig.scheduledStartAt)) {
      if (showToast) this.$store?.toastr?.warning?.('Choose a scheduled start date and time');
      return false;
    }
    const m = this.drawConfig.matchRules || {};
    const ss = Number(m.startingScore);
    if (!Number.isFinite(ss) || ss < 101 || ss > 1001) {
      if (showToast) this.$store?.toastr?.warning?.('Starting score must be between 101 and 1001');
      return false;
    }
    return true;
  },

  buildDrawConfigPayload() {
    this.normalizeMatchRulesLocal();
    const scheduled =
      this.drawConfig.startMode === 'scheduled'
        ? this.fromDatetimeLocal(this.drawConfig.scheduledStartAt)
        : null;
    return {
      startMode: this.drawConfig.startMode,
      scheduledStartAt: scheduled,
      defaultVenueId: idStr(this.drawConfig.defaultVenueId) || '',
      defaultBoardId: String(this.drawConfig.defaultBoardId || '').trim(),
      seedMode: this.drawConfig.seedMode,
      balanceByes: !!this.drawConfig.balanceByes,
      groupCount: Number(this.drawConfig.groupCount) || 2,
      knockoutSize: Number(this.drawConfig.knockoutSize) || 8,
      grandFinal: this.drawConfig.grandFinal,
      playMode: this.drawConfig.playMode === 'remote' ? 'remote' : 'local',
      matchRules: { ...this.drawConfig.matchRules },
    };
  },

  async saveDrawOptions() {
    if (!this.tournamentId) return false;
    if (!this.validateDrawConfig()) return false;
    this.saving = true;
    this.error = '';
    try {
      const res = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}`, {
        method: 'PATCH',
        body: { drawConfig: this.buildDrawConfigPayload() },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || data.message || 'Save failed');
      this.tournament = data.tournament;
      this.hydrateDrawConfig(data.tournament?.drawConfig);
      return true;
    } catch (e) {
      this.error = e.message || String(e);
      this.$store?.toastr?.error?.(this.error);
      return false;
    } finally {
      this.saving = false;
    }
  },

  entrantEntryTypeLabel() {
    const m = { player: 'players', team: 'teams', pair: 'pairs' };
    return m[this.form.entryType] || 'entrants';
  },

  closeEntrantSearch() {
    this.entrantSearch.open = false;
  },

  openEntrantSearch() {
    this.entrantSearch.open = true;
    if (!String(this.entrantSearch.q || '').trim()) {
      this.entrantSearch.apiResults = [];
    }
  },

  async searchEntrants() {
    const q = String(this.entrantSearch.q || '').trim();
    if (!q && !this.entrantSearch.useDirectory) {
      this.entrantSearch.apiResults = [];
      return;
    }
    this.entrantSearch.busy = true;
    try {
      if (this.form.entryType === 'team') {
        const needle = q.toLowerCase();
        this.entrantSearch.apiResults = this.teamOptions
          .filter((t) => !q || String(t.name || '').toLowerCase().includes(needle))
          .slice(0, 30);
      } else if (this.entrantSearch.useDirectory) {
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        params.set('limit', '30');
        if (this.entrantDirectoryFilterLevel) params.set('level', this.entrantDirectoryFilterLevel);
        if (Number(this.entrantDirectoryMinAvg) > 0) params.set('minAvg', String(this.entrantDirectoryMinAvg));
        const res = await apiFetch(`/players/directory?${params.toString()}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || data.error || 'Directory search failed');
        this.entrantSearch.apiResults = (Array.isArray(data.items) ? data.items : []).map((p) => ({
          ...p,
          _id: idStr(p._id || p.id),
        }));
      } else {
        const res = await apiFetch(
          `/players?q=${encodeURIComponent(q)}&limit=30&sortBy=fullName:asc&select=_id,fullName,nickname,email,imageUrl`,
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || data.error || 'Search failed');
        this.entrantSearch.apiResults = (Array.isArray(data.items) ? data.items : []).map((p) => ({
          ...p,
          _id: idStr(p._id || p.id),
        }));
      }
    } catch (e) {
      this.$store?.toastr?.error?.(e.message || 'Search failed');
      this.entrantSearch.apiResults = [];
    } finally {
      this.entrantSearch.busy = false;
    }
  },

  toggleEntrantDirectoryMode() {
    this.entrantSearch.useDirectory = !this.entrantSearch.useDirectory;
    if (this.entrantSearch.useDirectory || String(this.entrantSearch.q || '').trim()) {
      this.searchEntrants();
    }
  },

  closeQuickAddLookup() {
    this.quickAddPlayer.lookupOpen = false;
  },

  openQuickAddLookup() {
    this.quickAddPlayer.lookupOpen = true;
    if (!String(this.quickAddPlayer.lookupQuery || '').trim()) {
      this.quickAddPlayer.lookupResults = [];
    }
  },

  async searchQuickAddLookup() {
    const q = String(this.quickAddPlayer.lookupQuery || '').trim();
    if (!q || q.length < 2) {
      this.quickAddPlayer.lookupResults = [];
      return;
    }
    this.quickAddPlayer.lookupBusy = true;
    try {
      const res = await apiFetch(
        `/players?q=${encodeURIComponent(q)}&limit=8&sortBy=fullName:asc&select=_id,fullName,nickname,email,imageUrl`,
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || 'Search failed');
      this.quickAddPlayer.lookupResults = (Array.isArray(data.items) ? data.items : []).map((p) => ({
        ...p,
        _id: idStr(p._id || p.id),
      }));
    } catch (e) {
      this.$store?.toastr?.error?.(e.message || 'Search failed');
      this.quickAddPlayer.lookupResults = [];
    } finally {
      this.quickAddPlayer.lookupBusy = false;
    }
  },

  quickAddLookupResults() {
    return (this.quickAddPlayer.lookupResults || []).filter((row) => this.canPickEntrant(row));
  },

  quickAddLookupUnavailableCount() {
    return (this.quickAddPlayer.lookupResults || []).filter((row) => !this.canPickEntrant(row)).length;
  },

  async addQuickAddExisting(row) {
    if (await this.addEntrantRow(row)) {
      this.$store?.toastr?.success?.('Player added');
      this.quickAddPlayer.lookupQuery = '';
      this.quickAddPlayer.lookupResults = [];
      this.quickAddPlayer.lookupOpen = false;
    }
  },

  async quickCreateAndAddPlayer() {
    if (!this.tournamentId) return;
    const fullName = String(this.quickAddPlayer.fullName || '').trim();
    if (!fullName) {
      this.$store?.toastr?.warning?.('Enter a player name');
      return;
    }
    this.quickAddPlayer.busy = true;
    try {
      const email = String(this.quickAddPlayer.email || '').trim().toLowerCase();
      const body = {
        fullName,
        country: 'GB',
        status: 'pool',
        role: 'player',
      };
      if (email) {
        body.email = email;
        if (this.quickAddPlayer.sendInvite) body.sendInvite = true;
      }
      const res = await apiFetch('/players', { method: 'POST', body });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || 'Create player failed');
      const created = data.player || data.item || data;
      const pid = idStr(created._id || created.id);
      if (!pid) throw new Error('Create player failed');
      const row = { _id: pid, fullName: created.fullName || fullName, nickname: created.nickname || '' };
      if (!(this.playerOptions || []).some((p) => idStr(p._id) === pid)) {
        this.playerOptions.push(row);
      }
      if (await this.addEntrantRow(row)) {
        this.$store?.toastr?.success?.('Player created and added');
      }
      this.quickAddPlayer = {
        lookupQuery: '',
        lookupOpen: false,
        lookupBusy: false,
        lookupResults: [],
        fullName: '',
        email: '',
        sendInvite: false,
        busy: false,
      };
    } catch (e) {
      this.$store?.toastr?.error?.(e.message || String(e));
      this.quickAddPlayer.busy = false;
    }
  },

  entrantDefaultPool() {
    if (this.form.entryType === 'team') {
      return this.teamOptions.slice(0, 24);
    }
    return this.playerOptions.slice(0, 24);
  },

  entrantSearchPool() {
    const q = String(this.entrantSearch.q || '').trim();
    if ((q || this.entrantSearch.useDirectory) && this.entrantSearch.apiResults.length) {
      return this.entrantSearch.apiResults;
    }
    if (q && this.form.entryType === 'team') {
      const needle = q.toLowerCase();
      return this.teamOptions
        .filter((t) => String(t.name || '').toLowerCase().includes(needle))
        .slice(0, 30);
    }
    if (q && this.form.entryType !== 'team') {
      const needle = q.toLowerCase();
      return this.playerOptions
        .filter((p) => {
          const name = `${p.fullName || ''} ${p.nickname || ''} ${p.email || ''}`.toLowerCase();
          return name.includes(needle);
        })
        .slice(0, 30);
    }
    return this.entrantDefaultPool();
  },

  entrantSearchResultsForDisplay() {
    return this.entrantSearchPool().filter((row) => this.canPickEntrant(row));
  },

  entrantHiddenUnavailableCount() {
    return this.entrantSearchPool().filter((row) => !this.canPickEntrant(row)).length;
  },

  isPlayerInEntries(playerId) {
    const pid = idStr(playerId);
    if (!pid) return false;
    return this.entries.some((e) => {
      if (e.withdrawn) return false;
      const refs = Array.isArray(e.refIds) ? e.refIds.map(idStr) : [];
      return refs.includes(pid);
    });
  },

  isTeamInEntries(teamId) {
    const tid = idStr(teamId);
    if (!tid) return false;
    return this.entries.some((e) => {
      if (e.withdrawn || e.kind !== 'team') return false;
      const refs = Array.isArray(e.refIds) ? e.refIds.map(idStr) : [];
      return refs.includes(tid);
    });
  },

  canPickEntrant(row) {
    if (!row?._id) return false;
    if (this.form.entryType === 'team') return !this.isTeamInEntries(row._id);
    return !this.isPlayerInEntries(row._id);
  },

  entrantUnavailableHint(row) {
    if (this.form.entryType === 'team' && this.isTeamInEntries(row._id)) return 'Already entered';
    if (this.isPlayerInEntries(row._id)) return 'Already entered';
    return 'Available';
  },

  entrantRowLabel(row) {
    if (this.form.entryType === 'team') return row.name || row._id;
    const nick = row.nickname ? ` (${row.nickname})` : '';
    return `${row.fullName || row.name || row._id}${nick}`;
  },

  entrantRowSubtitle(row) {
    if (this.form.entryType === 'team') return '';
    const email = String(row.email || '').trim();
    if (email) return email;
    const nick = String(row.nickname || '').trim();
    if (nick && !String(row.fullName || '').toLowerCase().includes(nick.toLowerCase())) {
      return `@${nick}`;
    }
    return '';
  },

  entrantRowAvatar(row) {
    if (!row) return '/assets/placeholder-player.png';
    const url = String(row.imageUrl || row.logoUrl || row.photoUrl || '').trim();
    if (url) return url;
    return venuePersonaAvatarDataUri(idStr(row._id || 'entrant'));
  },

  entryAvatarUrl(entry) {
    if (!entry) return '/assets/placeholder-player.png';
    const refs = Array.isArray(entry.refIds) ? entry.refIds : [];
    if (entry.kind === 'team' && refs[0]) {
      const t = this.teamOptions.find((x) => idStr(x._id) === idStr(refs[0]));
      return this.entrantRowAvatar(t || { _id: refs[0] });
    }
    if (refs[0]) {
      const p = this.playerOptions.find((x) => idStr(x._id) === idStr(refs[0]));
      return this.entrantRowAvatar(p || { _id: refs[0] });
    }
    return venuePersonaAvatarDataUri(idStr(entry.id));
  },

  onEntrantAvatarError(ev) {
    const el = ev?.target;
    if (!el || el.dataset.fallbackApplied) return;
    el.dataset.fallbackApplied = '1';
    el.src = '/assets/placeholder-player.png';
  },

  isEntrantPickSelected(row) {
    return !!this.entrantPickSelected[idStr(row._id)];
  },

  toggleEntrantPickSelected(row) {
    if (!this.canPickEntrant(row)) return;
    const key = idStr(row._id);
    if (this.entrantPickSelected[key]) {
      const next = { ...this.entrantPickSelected };
      delete next[key];
      this.entrantPickSelected = next;
    } else {
      this.entrantPickSelected = { ...this.entrantPickSelected, [key]: true };
    }
  },

  entrantPickSelectedCount() {
    return Object.keys(this.entrantPickSelected || {}).filter(Boolean).length;
  },

  clearEntrantPickSelection() {
    this.entrantPickSelected = {};
  },

  selectAllEntrantSearchResults() {
    const next = { ...this.entrantPickSelected };
    for (const row of this.entrantSearchResultsForDisplay()) {
      if (this.canPickEntrant(row)) next[idStr(row._id)] = true;
    }
    this.entrantPickSelected = next;
  },

  nextAutoSeed() {
    const seeds = this.entries.map((e) => Number(e.seed)).filter((n) => Number.isFinite(n) && n > 0);
    if (!seeds.length) return this.entries.length + 1;
    return Math.max(...seeds) + 1;
  },

  async postEntry(body) {
    const res = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}/entries`, {
      method: 'POST',
      body,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.ok === false) throw new Error(data.error || data.message || 'Add entry failed');
    return data.entry;
  },

  buildEntryBodyForPlayer(playerId) {
    const id = idStr(playerId);
    const p = this.playerOptions.find((x) => idStr(x._id) === id)
      || this.entrantSearch.apiResults.find((x) => idStr(x._id) === id);
    return {
      kind: 'player',
      refIds: [id],
      displayName: p?.fullName || p?.name || id,
      seed: this.nextAutoSeed(),
    };
  },

  buildEntryBodyForTeam(teamId) {
    const id = idStr(teamId);
    const t = this.teamOptions.find((x) => idStr(x._id) === id)
      || this.entrantSearch.apiResults.find((x) => idStr(x._id) === id);
    return {
      kind: 'team',
      refIds: [id],
      displayName: t?.name || id,
      seed: this.nextAutoSeed(),
    };
  },

  async addEntrantRow(row) {
    if (!this.tournamentId || !row?._id) return false;
    if (!this.canPickEntrant(row)) return false;
    try {
      const body =
        this.form.entryType === 'team'
          ? this.buildEntryBodyForTeam(row._id)
          : this.buildEntryBodyForPlayer(row._id);
      const entry = await this.postEntry(body);
      this.entries.push(entry);
      return true;
    } catch (e) {
      this.$store?.toastr?.error?.(e.message || String(e));
      return false;
    }
  },

  async addSelectedEntrants() {
    if (!this.tournamentId) return;
    const keys = Object.keys(this.entrantPickSelected || {}).filter((k) => this.entrantPickSelected[k]);
    if (!keys.length) {
      this.$store?.toastr?.warning?.('Select one or more first');
      return;
    }
    let added = 0;
    for (const row of this.entrantSearchPool()) {
      const rid = idStr(row._id);
      if (!this.entrantPickSelected[rid]) continue;
      if (await this.addEntrantRow(row)) added += 1;
    }
    this.clearEntrantPickSelection();
    if (added) {
      this.$store?.toastr?.success?.(added === 1 ? '1 entrant added' : `${added} entrants added`);
    } else {
      this.$store?.toastr?.info?.('No entrants were added (may already be entered)');
    }
  },

  setPairPickSlot(slot, playerId) {
    const pid = idStr(playerId);
    if (slot === 'a') this.pairPick.a = pid;
    else this.pairPick.b = pid;
  },

  pairPickLabel(slot) {
    const pid = slot === 'a' ? this.pairPick.a : this.pairPick.b;
    if (!pid) return '';
    const p = this.playerOptions.find((x) => idStr(x._id) === pid);
    return p ? this.entrantRowLabel(p) : pid;
  },

  async addPairEntry() {
    if (!this.tournamentId) return;
    const a = idStr(this.pairPick.a);
    const b = idStr(this.pairPick.b);
    if (!a || !b || a === b) {
      this.$store?.toastr?.warning?.('Pick two different players');
      return;
    }
    if (this.isPlayerInEntries(a) || this.isPlayerInEntries(b)) {
      this.$store?.toastr?.warning?.('One or both players are already entered');
      return;
    }
    const pa = this.playerOptions.find((x) => idStr(x._id) === a);
    const pb = this.playerOptions.find((x) => idStr(x._id) === b);
    try {
      const entry = await this.postEntry({
        kind: 'pair',
        refIds: [a, b],
        displayName: `${pa?.fullName || pa?.name || 'A'} & ${pb?.fullName || pb?.name || 'B'}`,
        seed: this.nextAutoSeed(),
      });
      this.entries.push(entry);
      this.pairPick = { a: '', b: '' };
      this.$store?.toastr?.success?.('Pair added');
    } catch (e) {
      this.$store?.toastr?.error?.(e.message || String(e));
    }
  },

  async createTournament() {
    this.saving = true;
    this.error = '';
    try {
      const res = await apiFetch('/tournaments', {
        method: 'POST',
        body: {
          title: this.form.title,
          format: this.form.format,
          entryType: this.form.entryType,
          notes: this.form.notes,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || data.error || 'Create failed');
      const id = data.tournament?.id;
      if (!id) throw new Error('No tournament id');
      navTo(`/admin/tournaments/${encodeURIComponent(id)}/builder`);
    } catch (e) {
      this.error = e.message || 'Create failed';
      this.$store?.toastr?.error?.(this.error);
    } finally {
      this.saving = false;
    }
  },

  async phaseRegistration() {
    if (!this.tournamentId) return;
    this.error = '';
    try {
      const res = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}`, {
        method: 'PATCH',
        body: { phase: 'registration' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || 'PATCH failed');
      this.tournament = data.tournament;
      this.$store?.toastr?.success?.('Registration phase open — you can add entrants.');
    } catch (e) {
      this.error = e.message || String(e);
      this.$store?.toastr?.error?.(this.error);
    }
  },

  async deleteTournament() {
    if (!this.tournamentId || !this.canDelete()) return;
    const title = this.tournament?.title || this.form.title || 'this tournament';
    if (!globalThis.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    this.deleting = true;
    try {
      const res = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || data.error || 'Delete failed');
      this.$store?.toastr?.success?.('Tournament deleted');
      navTo('/admin/tournaments');
    } catch (e) {
      this.$store?.toastr?.error?.(e.message || 'Delete failed');
    } finally {
      this.deleting = false;
    }
  },

  async endTournamentSession() {
    if (!this.tournamentId || !this.canEndSession()) return;
    const title = this.tournament?.title || this.form.title || 'this tournament';
    if (!globalThis.confirm(`End "${title}" now?`)) return;
    this.ending = true;
    try {
      const res = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}/end`, { method: 'POST', body: {} });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || data.error || 'Could not end tournament');
      this.tournament = data.tournament || this.tournament;
      this.$store?.toastr?.success?.('Tournament ended');
      navTo(`/admin/tournaments/${encodeURIComponent(this.tournamentId)}`);
    } catch (e) {
      this.$store?.toastr?.error?.(e.message || 'Could not end tournament');
    } finally {
      this.ending = false;
    }
  },

  async removeEntry(entryId) {
    if (!this.tournamentId || !entryId) return;
    try {
      const res = await apiFetch(
        `/tournaments/${encodeURIComponent(this.tournamentId)}/entries/${encodeURIComponent(entryId)}`,
        { method: 'DELETE' },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || 'Remove failed');
      this.entries = this.entries.filter((e) => e.id !== entryId);
    } catch (e) {
      this.$store?.toastr?.error?.(e.message || String(e));
    }
  },

  async generateBracket() {
    if (!this.tournamentId) return;
    if (this.activeEntries().length < 2) {
      this.$store?.toastr?.warning?.('Need at least 2 entrants');
      return;
    }
    if (!this.validateDrawConfig()) return;
    const saved = await this.saveDrawOptions();
    if (!saved) return;
    this.generating = true;
    this.error = '';
    try {
      const dc = this.buildDrawConfigPayload();
      const res = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}/generate-bracket`, {
        method: 'POST',
        body: {
          drawConfig: dc,
          groupCount: dc.groupCount,
          knockoutSize: dc.knockoutSize,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || data.error || 'Generate failed');
      this.tournament = data.tournament;
      this.entries = Array.isArray(data.entries) ? data.entries : this.entries;
      this.$store?.toastr?.success?.('Bracket generated');
      const twoPlayerNow = this.activeEntries().length === 2 && this.drawConfig.startMode === 'now';
      const q = twoPlayerNow ? '?highlight=play' : '';
      navTo(`/admin/tournaments/${encodeURIComponent(this.tournamentId)}/bracket${q}`);
    } catch (e) {
      this.error = e.message || String(e);
      this.$store?.toastr?.error?.(this.error);
    } finally {
      this.generating = false;
    }
  },
}));
